import { Box, Spinner, Tabs } from '@zillow/constellation-10';
import { MainLayoutProvider, MainLayoutType, useMainLayout } from './MainLayoutContext';
import { ZRAMPageLayout } from '../ZRAMPageLayout';
import { useRouter } from 'next/router';
import { tabsConfig, TabsEnum } from './MainPageLayout.config';
import { PropertiesAndLayoutTypeTopBar } from './PropertiesAndLayoutTypeTopBar';
import { TopBarDropdowns } from './TopBarDropdowns';
import { useEffect, useRef } from 'react';
import { getActiveTabFromPathname } from './MainPageLayout.utils';
import dynamic from 'next/dynamic';
import { PageLoading } from '../../SharedComponents/PageLoading';
import { zillowLoginRedirectHref } from '@zg-rentals/login-zillow/client';
import { Auth, useAuth } from '../../../AuthProvider';
import {
  emitAnalyticsNavAdSpendClickEvent,
  emitAnalyticsNavIndustryReportsClickEvent,
  emitAnalyticsNavMarketDataClickEvent,
  emitAnalyticsNavOverviewClickEvent,
  emitAnalyticsNavRenterDataClickEvent,
  emitPageviewAnalyticsAdSpendEvent,
  emitPageviewAnalyticsIndustryReportsEvent,
  emitPageviewAnalyticsMarketCompsEvent,
  emitPageviewAnalyticsMarketDataEvent,
  emitPageviewAnalyticsOverviewEvent,
  emitPageviewAnalyticsRenterDataEvent,
} from '../../../analytics/events';
import type { ZrmClickstreamUser } from '@zg-rentals/clickstream';
import { OverviewReport } from '../../containers/Overview';
import { AdSpendReport } from '../../containers/AdSpend';
import { MarketDataReport } from '../../containers/MarketData';
import { RenterDataReport } from '../../containers/RenterData';
import { IndustryReports } from '../../containers/IndustryReports';
import { MarketComps } from '../../containers/MarketComps';
import { NewVersionToggle } from '@zg-rentals/rental-revenue-components';

const OverviewMap = dynamic(() => import('../../containers/Overview').then((mod) => mod.OverviewMap), {
  ssr: false,
  loading: () => <PageLoading />,
});

const AdSpendMap = dynamic(() => import('../../containers/AdSpend').then((mod) => mod.AdSpendMap), {
  ssr: false,
  loading: () => <PageLoading />,
});

const MarketDataMap = dynamic(() => import('../../containers/MarketData').then((mod) => mod.MarketDataMap), {
  ssr: false,
  loading: () => <PageLoading />,
});

const ALL_PAGES_WITH_MAP = [TabsEnum.OVERVIEW, TabsEnum.AD_SPEND, TabsEnum.MARKET_DATA];

export const usePagesWithMap = () => {
  const { user } = useAuth();
  const isImpersonator = Boolean(user?.impersonatorUserToken || user?.impersonatorEmail);
  return isImpersonator ? [] : ALL_PAGES_WITH_MAP;
};

const MainPageLayoutSkeleton = () => {
  return (
    <Box css={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Box
        css={{
          minHeight: '217px',
          backgroundColor: 'white',
          borderBottom: '1px solid',
          borderColor: 'border.muted',
        }}
      />
      <Box
        css={{
          backgroundColor: 'gray.50',
          flexGrow: 1,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Spinner size="lg" />
      </Box>
    </Box>
  );
};

const getPageComponent = (activeTab: TabsEnum, layout: MainLayoutType, pagesWithMap: Array<TabsEnum>) => {
  if (layout === MainLayoutType.MAP && pagesWithMap.includes(activeTab)) {
    switch (activeTab) {
      case TabsEnum.OVERVIEW:
        return <OverviewMap />;
      case TabsEnum.AD_SPEND:
        return <AdSpendMap />;
      case TabsEnum.MARKET_DATA:
        return <MarketDataMap />;
    }
  }

  switch (activeTab) {
    case TabsEnum.OVERVIEW:
      return <OverviewReport />;
    case TabsEnum.AD_SPEND:
      return <AdSpendReport />;
    case TabsEnum.MARKET_DATA:
      return <MarketDataReport />;
    case TabsEnum.RENTER_DATA:
      return <RenterDataReport />;
    case TabsEnum.MARKET_REPORTS:
      return <IndustryReports />;
    case TabsEnum.MARKET_COMPS:
      return <MarketComps />;
    default:
      return <OverviewReport />;
  }
};

const MainPageLayoutContent = () => {
  const router = useRouter();
  const activeTab = getActiveTabFromPathname(router.pathname);

  const { isFetchingInitialData, layout, allProperties } = useMainLayout();
  const { user } = useAuth();
  const headerRef = useRef<HTMLDivElement>(null);

  const isImpersonator = Boolean(user?.impersonatorUserToken || user?.impersonatorEmail);
  const pagesWithMap = usePagesWithMap();

  const isAllSingleFamily = allProperties.length > 0 && allProperties.every((p) => p.aggregateStats.isSingleFamily);
  const hasAtLeastOneMultifamily =
    allProperties.length > 0 && allProperties.some((p) => !p.aggregateStats.isSingleFamily);

  const filterIndustryReports = ({ tab }: { tab: TabsEnum }) => tab !== TabsEnum.MARKET_REPORTS || !isAllSingleFamily;

  const filterMarketComps = ({ tab }: { tab: TabsEnum }) =>
    tab !== TabsEnum.MARKET_COMPS || (isImpersonator && hasAtLeastOneMultifamily);

  const filterTabs = (item: { tab: TabsEnum }) => filterIndustryReports(item) && filterMarketComps(item);

  /*useEffect(() => {
    if (savedViews && savedViews.length > 0 && allProperties.length > 0 && !defaultViewApplied) {
      const defaultView = savedViews.find((view) => view.isDefault);
      if (defaultView) {
        applySavedViewToContext({
          view: defaultView,
          setSelectedFilters,
          setLayout,
          setZoom,
          setCenter,
          allProperties,
          setShouldForceBoundsToGeojsonBounds,
        });
        setDefaultViewApplied(true);
      }
    }
  }, [
    savedViews,
    allProperties,
    setSelectedFilters,
    setLayout,
    setZoom,
    setCenter,
    setShouldForceBoundsToGeojsonBounds,
    defaultViewApplied,
  ]);*/

  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      document.documentElement.style.setProperty('--header-height', `${entry.contentRect.height}px`);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    tabsConfig.forEach(({ tab }) => {
      router.prefetch(`/${tab}`);
    });
  }, [router]);

  useEffect(() => {
    if (!user) return;
    const pageviewEmitMap: Record<TabsEnum, (args: { user: ZrmClickstreamUser }) => void> = {
      [TabsEnum.OVERVIEW]: emitPageviewAnalyticsOverviewEvent,
      [TabsEnum.MARKET_DATA]: emitPageviewAnalyticsMarketDataEvent,
      [TabsEnum.RENTER_DATA]: emitPageviewAnalyticsRenterDataEvent,
      [TabsEnum.AD_SPEND]: emitPageviewAnalyticsAdSpendEvent,
      [TabsEnum.MARKET_REPORTS]: emitPageviewAnalyticsIndustryReportsEvent,
      [TabsEnum.MARKET_COMPS]: emitPageviewAnalyticsMarketCompsEvent,
    };
    pageviewEmitMap[activeTab]({ user });
  }, [activeTab, user]);

  return (
    <ZRAMPageLayout>
      <Box
        css={{ display: 'flex', flexDirection: 'column', height: '100vh', position: 'relative', overflow: 'hidden' }}
      >
        <Box
          ref={headerRef}
          css={{
            backgroundColor: 'white',
            overflow: 'visible',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            minHeight: '217px',
            width: '100%',
            zIndex: 50,
          }}
        >
          {isFetchingInitialData && (
            <Box
              css={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 1,
                backgroundColor: 'white',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 3,
              }}
            >
              <Spinner size="lg" />
            </Box>
          )}
          <PropertiesAndLayoutTypeTopBar activeTab={activeTab} />
          <TopBarDropdowns />
          <Tabs.Root
            selected={activeTab}
            css={{
              display: 'flex',
              borderBottom: '1px solid',
              borderColor: 'border.muted !important',
              paddingInline: '900',
              paddingBlockStart: '600',
            }}
            onSelectedChange={(val) => {
              const tab = val as TabsEnum;
              if (user) {
                const tabClickEmitMap: Partial<Record<TabsEnum, (args: { user: ZrmClickstreamUser }) => void>> = {
                  [TabsEnum.OVERVIEW]: emitAnalyticsNavOverviewClickEvent,
                  [TabsEnum.MARKET_DATA]: emitAnalyticsNavMarketDataClickEvent,
                  [TabsEnum.RENTER_DATA]: emitAnalyticsNavRenterDataClickEvent,
                  [TabsEnum.AD_SPEND]: emitAnalyticsNavAdSpendClickEvent,
                  [TabsEnum.MARKET_REPORTS]: emitAnalyticsNavIndustryReportsClickEvent,
                };
                tabClickEmitMap[tab]?.({ user });
              }
              router.push(`/${tab}`);
            }}
          >
            {tabsConfig.filter(filterTabs).map(({ tab, label }) => (
              <Tabs.Tab value={tab} key={tab} disabled={!pagesWithMap.includes(tab) && layout === MainLayoutType.MAP}>
                {label}
              </Tabs.Tab>
            ))}
          </Tabs.Root>
        </Box>
        <Box
          css={{
            backgroundColor: 'gray.50',
            flexGrow: 1,
            display: 'flex',
            marginTop: 'var(--header-height)',
            overflowY: 'auto',
          }}
        >
          {getPageComponent(activeTab, layout, pagesWithMap)}
        </Box>
      </Box>
      {user && isImpersonator && user.isSingleFamilyEnterpriseCustomer && (
        <NewVersionToggle text="Enable old version" />
      )}
    </ZRAMPageLayout>
  );
};

const zillowLoginRedirect = zillowLoginRedirectHref({
  baseURL: process.env.NEXT_PUBLIC_AUTH_BASE_URL,
  entryPoint: 'zram-customer-analytics-web',
  forwardPrefix: process.env.NEXT_PUBLIC_AUTH_FORWARD_PREFIX,
  forceLogin: true,
});

export const MainPageLayout = () => {
  return (
    <MainLayoutProvider>
      <Auth.Boundary redirectHref={zillowLoginRedirect} loading={<MainPageLayoutSkeleton />}>
        <MainPageLayoutContent />
      </Auth.Boundary>
    </MainLayoutProvider>
  );
};
